import React from 'react';
import { useDispatch } from 'react-redux';
import Typography from '@mui/material/Typography';
import IconButton from '@mui/material/IconButton';
import { styled } from '@mui/system';
import Tags from 'components/Tags';
import LazyLoadImage from 'components/LazyLoadImage';
import StarIcon from 'assets/icons/star.svg?react';
import StarCheckedIcon from 'assets/icons/star_checked.svg?react';
import { toggleFavorite } from 'actions/profession';
import { getLanguage } from 'translation';
import NeedAuthDialog from 'components/NeedAuthDialog';
import { isAuthenticated } from 'helpers/authToken';
import CustomLink from './CustomLink';
import { IS_SNAP } from 'variables/main';

const Wrapper = styled('div')(({ borderedCard, theme }) => ({
  borderRadius: '10px',
  border:
    borderedCard === 'true'
      ? `1px solid ${theme.palette.primary.main}`
      : '1px solid rgba(34, 34, 34, 0.1)',
  backgroundColor: theme.palette.white.main,
  overflow: 'hidden',
  transition: 'box-shadow 0.3s',
  position: 'relative',
  maxWidth: 520,
  width: '100%',
  height: '100%',
  '&:hover': {
    cursor: 'pointer',
    boxShadow: '0px 4px 10px rgba(0, 0, 0, 0.1)'
  },
  [theme.breakpoints.down('sm')]: {
    maxWidth: '100%'
  }
}));

const Title = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  color: theme.palette.textPrimary.main,
  marginBottom: '10px',
  ...theme.textCut('2'),
  [theme.breakpoints.down('md')]: {
    fontSize: 24,
    lineHeight: '28px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 18,
    lineHeight: '18px'
  }
}));

const Description = styled(Typography)(({ theme }) => ({
  textAlign: 'left',
  color: 'rgba(34, 34, 34, 0.6)',
  fontSize: 15,
  lineHeight: '20px',
  ...theme.textCut('5'),
  [theme.breakpoints.down('md')]: {
    fontSize: 14,
    lineHeight: '18px'
  },
  [theme.breakpoints.down('sm')]: {
    fontSize: 13,
    lineHeight: '15px'
  }
}));

const TextWrapper = styled('div')(({ theme }) => ({
  padding: 20,
  paddingTop: 10,
  [theme.breakpoints.down('sm')]: {
    padding: 14,
    paddingTop: 10,
    paddingBottom: 20
  }
}));

const FavoriteButton = styled(IconButton)(() => ({
  position: 'absolute',
  top: 10,
  right: 10,
  zIndex: 10
}));

const ImageSlot = styled('div')(({ theme }) => ({
  width: '100%',
  height: 250,
  backgroundColor: theme.palette.grey[100],
  overflow: 'hidden',
  [theme.breakpoints.down('sm')]: {
    height: 200
  }
}));

const CardWrapper = styled('div')(({ theme, columns }) => ({
  display: 'grid',
  gridTemplateColumns: `repeat(${columns || 3}, 1fr)`,
  gap: 20,
  marginBottom: 20,
  [theme.breakpoints.down('md')]: {
    gridTemplateColumns: `repeat(${columns || 2}, 1fr)`
  },
  [theme.breakpoints.down('sm')]: {
    gridTemplateColumns: '1fr'
  }
}));

const Card = (props) => {
  const [lang] = React.useState(getLanguage().toUpperCase());
  const dispatch = useDispatch();

  const {
    useFavorites,
    photoLinks,
    postImage,
    isFavorite: isFavoriteProp,
    tags,
    _id,
    endPoint = '',
    template,
    favoritesUrl,
    getRedirectUrl,
    universityName,
    number,
    externalLink,
    borderedCard
  } = React.useMemo(() => props, [props]);

  const [isFavorite, setIsFavorite] = React.useState(isFavoriteProp);
  const [needAuth, setNeedAuth] = React.useState(false);
  const title = React.useMemo(
    () => props[`name${lang}`] || props?.title || props?.name,
    [lang, props]
  );
  const description = React.useMemo(
    () => props[`descriptionShort${lang}`] || props?.descriptionShort,
    [lang, props]
  );
  const image = React.useMemo(() => photoLinks?.main || postImage, [postImage, photoLinks]);

  const handleToggleFavorite = React.useCallback(
    (e) => {
      e.stopPropagation();
      e.preventDefault();

      if (!isAuthenticated()) {
        setNeedAuth(true);
        return;
      }

      dispatch(toggleFavorite(_id, isFavorite, `${endPoint}${favoritesUrl || ''}`));
      setIsFavorite(!isFavorite);
    },
    [dispatch, _id, isFavorite, endPoint, favoritesUrl]
  );

  const handleClose = React.useCallback((e) => {
    e.stopPropagation();
    e.preventDefault();
    setNeedAuth(false);
  }, []);

  return (
    <CustomLink
      externalLink={externalLink}
      getRedirectUrl={getRedirectUrl}
      endPoint={endPoint}
      title={title}
      number={number}
      universityName={universityName}
      lang={lang}
      _id={_id}
      props={props}>
      <Wrapper borderedCard={`${borderedCard}`}>
        {useFavorites ? (
          <>
            <FavoriteButton onClick={handleToggleFavorite} aria-label="favorite" size="small">
              {isFavorite ? <StarCheckedIcon /> : <StarIcon />}
            </FavoriteButton>
            <NeedAuthDialog handleClose={handleClose} needAuth={needAuth} />
          </>
        ) : null}
        {/* Avoid loading remote images during react-snap prerendering to prevent Chromium OOM/crashes */}
        <ImageSlot>
          {!IS_SNAP && image ? (
            <LazyLoadImage
              alt={title}
              src={`${image}/w400h250`}
              width="100%"
              height="100%"
              style={{ width: '100%', height: '100%', objectFit: 'cover' }}
            />
          ) : null}
        </ImageSlot>
        <TextWrapper>
          {template ? (
            template(props)
          ) : (
            <>
              <Title variant="h3" title={title}>
                {title}
              </Title>
              <Tags list={tags} limit={3} isCard={true} />
              <Description variant="body1">{description}</Description>
            </>
          )}
        </TextWrapper>
      </Wrapper>
    </CustomLink>
  );
};

const Cards = ({
  list,
  columns,
  useFavorites = true,
  template,
  getRedirectUrl,
  endPoint = 'professions',
  externalLink,
  borderedCard,
  newTab
}) => {
  if (!list || !list.length) return null;

  return (
    <CardWrapper columns={columns}>
      {list.map((card) => (
        <Card
          key={card._id}
          useFavorites={useFavorites}
          endPoint={endPoint}
          getRedirectUrl={getRedirectUrl}
          template={template}
          externalLink={externalLink}
          borderedCard={borderedCard}
          newTab={newTab}
          {...card}
        />
      ))}
    </CardWrapper>
  );
};

export { Card };

export default Cards;
